import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import SignalDetail from '@/pages/SignalDetail';
import Videos from '@/pages/Videos';
import Resources from '@/pages/Resources';
import EconomicNews from '@/pages/EconomicNews';
import LiveTrading from '@/pages/LiveTrading';
import Social from '@/pages/Social';
import ResetPassword from '@/pages/ResetPassword';
import AdminSignals from '@/pages/AdminSignals';
import { Toaster } from '@/components/ui/sonner';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import { X } from 'lucide-react';
import axios from 'axios';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#020402] flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent mb-4"></div>
      <p className="text-emerald-100/70">Cargando...</p>
    </div>
  </div>
);

function OnboardingVideoModal() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    if (!user) return;
    const key = `onboarding_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    // Fetch shorts to find onboarding video
    axios.get(`${API}/videos?category=shorts`).then(res => {
      const onboardingVideo = res.data.find(v =>
        v.title.toUpperCase().includes('COMO UTILIZAR') || v.title.toUpperCase().includes('CÓMO UTILIZAR')
      );
      if (onboardingVideo) {
        // Build embed URL
        let embedUrl = onboardingVideo.url;
        if (onboardingVideo.platform === 'youtube') {
          const videoId = onboardingVideo.url.split('v=')[1]?.split('&')[0] || onboardingVideo.url.split('/').pop();
          embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
        }
        setVideoUrl(embedUrl);
        setShow(true);
      }
    }).catch(() => {});
  }, [user]);

  const handleClose = () => {
    setShow(false);
    if (user) localStorage.setItem(`onboarding_seen_${user.id}`, '1');
  };

  if (!show || !videoUrl) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="relative w-full max-w-4xl">
        <button
          onClick={handleClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white flex items-center space-x-2 z-10"
          data-testid="onboarding-close"
        >
          <span className="text-sm">Cerrar</span>
          <X className="h-6 w-6" />
        </button>
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold text-emerald-400" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            BIENVENIDO A TRADING ALIENÍGENA
          </h2>
          <p className="text-emerald-100/70 text-sm">Mira este video para aprender a usar la plataforma</p>
        </div>
        <div className="rounded-lg overflow-hidden border border-emerald-500/30" style={{ aspectRatio: '16/9' }}>
          <iframe
            src={videoUrl}
            title="Onboarding"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
            data-testid="onboarding-video"
          />
        </div>
        <p className="text-center text-emerald-100/40 text-xs mt-3">Este video solo se muestra una vez. Puedes encontrarlo en la sección de Videos → Shorts.</p>
      </div>
    </div>
  );
}

function OperativaGuard({ children }) {
  const { user } = useAuth();
  if (user?.subscription_type === 'operativa') {
    return <Navigate to="/live" replace />;
  }
  return children;
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <SubscriptionBanner />
      <OnboardingVideoModal />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/social" element={<ProtectedRoute><OperativaGuard><Social /></OperativaGuard></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><OperativaGuard><Dashboard /></OperativaGuard></ProtectedRoute>} />
        <Route path="/signals/:id" element={<ProtectedRoute><OperativaGuard><SignalDetail /></OperativaGuard></ProtectedRoute>} />
        <Route path="/videos" element={<ProtectedRoute><OperativaGuard><Videos /></OperativaGuard></ProtectedRoute>} />
        <Route path="/resources" element={<ProtectedRoute><OperativaGuard><Resources /></OperativaGuard></ProtectedRoute>} />
        <Route path="/economic-news" element={<ProtectedRoute><OperativaGuard><EconomicNews /></OperativaGuard></ProtectedRoute>} />
        <Route path="/live" element={<ProtectedRoute><LiveTrading /></ProtectedRoute>} />
        <Route path="/admin/signals" element={<AdminRoute><AdminSignals /></AdminRoute>} />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;